// 覚醒王・大暴れ第5案（2026-09-19 ユーザー指定）の振付データ。
// モック (awoken_rampage_probe.ts) と本編 (boss.ts の choreography 分岐) が同じ表を読む。
// 高さは床からの px。本編の覚醒王は画面上で約186px (PLAYER_DISPLAY_HEIGHT 120 × 1.55) あるので、
// 頭を画面内に収めるため経路全体で CHOREO_MAX_ALTITUDE 以下にする (tests/awoken_rampage_choreo.test.ts)。
// 設計＝docs/handoffs/2026-09-19-awoken-rampage-v5-implementation-ticket.md §5

import { arenaBoundsFor } from './balance'

export type ChoreoLeg = {
  phase: number
  kind: 'run' | 'bite' | 'follow'
  x: number
  y: number
  // 放物線の山の高さ (重力のある跳び方)。0 なら直線。
  arc: number
  // 区間の頭の「止まり」。床なら屈み、壁ならしがみつき (溜め)。
  warning: number
  travel: number
  // 壁を蹴った瞬間 (travel の頭) に出す怨念。各々「蹴ってから何 ms で主人公に届くか」と、弾けない (赤) か。
  spirits?: readonly SpiritCue[]
  // 着地 (travel の終わり) で床を這う衝撃波を左右へ出す。弾けない＝回避で抜ける (床技・採用未定 09-19)。
  wave?: boolean
  // 区間の跳び出し (travel の頭) の GLINT_LEAD_SEC 前に目が光る合図 (09-20 ユーザー「ピカンと光って」)。
  //   赤＝弾けない技 (床技)・白＝弾ける大技 (締めの噛み)。全ボス共通の「赤＝避ける」に合わせた。
  glint?: 'red' | 'white'
}
export type SpiritCue = { arriveAfterKickMs: number; red?: boolean }

export const CHOREO_MAX_ALTITUDE = 230
// 壁にしがみつく位置 (足元の x)。右から入る型の基準で、左から入る時は鏡写し。
//   09-21: 885/75 → 794/166。壁面 (x=900/60) から `wall_kick` の後ろ足までの距離 106px だけ内側へ
//   (`ak-wall-rush` の wallContactInsetPx と同じ値)。885 だと体の4〜5割が画面の外へ出て、
//   顔と前脚しか映らなかった (09-21 総評 A-3)。後ろ足の裏がちょうど壁面に付く。
export const WALL_SURFACE_R = 900
export const WALL_SURFACE_L = 60
export const WALL_FOOT_INSET_PX = 106
export const WALL_R = WALL_SURFACE_R - WALL_FOOT_INSET_PX
export const WALL_L = WALL_SURFACE_L + WALL_FOOT_INSET_PX
// 今の舞台の壁面 (柱の面)。台本は 960 の舞台・壁面 60/900 で書いてあり、世界へ写す時 (mirrorX) に今の壁へ合わせて横に伸ばす。
//   10-27 覚醒王の舞台 1.4 倍 (src/awoken_wide_stage.ts) で壁面が -108/1068 へ動いた時、作者「3幕の大技が柱を使わずに攻撃している」。
//   壁から後ろ足までの 106px は伸ばさない (壁にしがみつく位置 = 今の壁面 − 106)。舞台1倍では 1px も変わらない。
export function choreoWallSurfaces(): { left: number; right: number } {
  const b = arenaBoundsFor('crowned-king-awoken')
  return { left: b.leftWall, right: b.rightWall }
}

// 壁しがみつきの見え方 (2026-09-20 ユーザー裁定「A」= `kick` を既定へ)。比較は DEV の `?wallPose=`。
//   kick = 既存の壁蹴りの絵 `wall_kick` をそのまま (傾けない・四肢の裏を壁へ貼り付ける・ak-wall-rush と同じ置き方)
//   now  = 第5版まで (跳びの絵を足元を軸に 75° 倒す → 体が壁の外へ振り出され、画面には前足しか残らない。
//          2026-09-20 実画面で確認。足元が回転の軸なので、傾けるほど体が横へ出る)
//   neg  = 同じ傾きを逆向きへ (全身は映るが「飛び降り途中」に見える)
//   kick20 = kick を 20° 傾けて「駆け上がる」寄り (爪が壁から少し浮く)
export type WallPoseVariant = 'now' | 'neg' | 'kick' | 'kick20'
const WALL_POSE_VALUES: readonly WallPoseVariant[] = ['now', 'neg', 'kick', 'kick20']
export const wallPoseVariant: WallPoseVariant = (() => {
  if (!import.meta.env.DEV || typeof window === 'undefined') return 'kick'
  const v = new URLSearchParams(window.location.search).get('wallPose')
  return WALL_POSE_VALUES.includes(v as WallPoseVariant) ? v as WallPoseVariant : 'kick'
})()
/** 壁しがみつきで壁の絵 (`wall_kick`) を使う案か。使う案は壁に背を向けず、頭を部屋側へ出す。 */
export const wallPoseUsesKickArt = (v: WallPoseVariant = wallPoseVariant): boolean => v === 'kick' || v === 'kick20'

// 横切りの噛み (幕6) の絵 (2026-09-21 ユーザー「攻撃か移動か分かりにくくなった・前のほうが良かった」)。
//   原因 = 09-20 に跳び出しを移動と同じ `rampage_leap` (牙なし) にしたため、噛む瞬間 (飛行の55%) まで移動と同じ姿だった。
//   mix = 跳び出しを口を開けた構え (牙あり) へ戻し、噛む瞬間だけ新しい横伸びの絵 (既定)
//   old = 09-20 より前そのまま (構え → 既存の噛みの絵)
//   leap = 09-20 の版 (跳びの絵 → 横伸びの噛み)。比較は DEV の `?swoopLook=`。
export type SwoopLook = 'mix' | 'old' | 'leap'
export const swoopLook: SwoopLook = (() => {
  if (!import.meta.env.DEV || typeof window === 'undefined') return 'mix'
  const v = new URLSearchParams(window.location.search).get('swoopLook')
  return v === 'old' || v === 'leap' ? v : 'mix'
})()

// 跳びの体の傾き (DEV 比較用・既定 'tangent')。'tangent' = 軌道に沿う (上がる時は頭が上・落ちる時は頭が下)。
//   'old' = 09-21 までの向き (符号が逆で、壁へ跳び上がる時に頭が床を向いていた)。`?v5Pitch=old` で見比べる。
export type PitchLook = 'tangent' | 'old'
export const pitchLook: PitchLook = (() => {
  if (!import.meta.env.DEV || typeof window === 'undefined') return 'tangent'
  return new URLSearchParams(window.location.search).get('v5Pitch') === 'old' ? 'old' : 'tangent'
})()

// 2026-09-19 第2版 (ユーザー指摘「吊るされてるみたい→壁ジャンプで移動」「分かれば95%以上被弾ゼロ」)。
// 2026-09-19 第4版 (ユーザー「もう少し難しく」「弾ばかりで本体が壁に張り付いているだけ→有効活用」
//   → 裁定「低く横切って噛む」「締め前の約2秒は床技に置き換え」「床技は着地の衝撃波 (回避だけ)・採用未定」)。
//   3往復＝壁を蹴るたびに主人公へ低く飛び込んで噛み、そのまま向こうの壁へ跳ぶ。
//   怨念は蹴った壁に残り、**次の横切りの間**に届く (本体が先・怨念が後)。1往復の拍＝1.35秒:
//     蹴り → 噛み (約0.3秒後) → 怨念 (0.75) → 怨念 (1.2) → 次の噛み (1.65)。赤は0.75/1.2の代わりに1.1へ1個。
//   どの瞬間も正解は1つ。拍の間は0.45秒以上、赤の前は0.8・後は0.55。
// 2026-09-21 第6版 = 1つの譜面にする (ユーザー「ボス本体のパリィも含めて1つの譜面として、正しく弾けているとリズムよく
//   気持ちいいように。単調ではなく抑揚のあるリズムに」「壁際は着弾が早すぎて反応できない」)。
//   ・拍の単位 BEAT_SEC = 0.225秒 (8分音符)。横切り1往復 = 6拍 = 1.35秒 = 1小節 (6/8)。小節の頭 (0拍目) が噛み。
//   ・噛みが当たる瞬間は「跳び出しから travel×BITE_STRIKE_PROGRESS 後」に固定 (立ち位置で変わらない・choreoPoseAt)。
//   ・怨念が当たる瞬間は「次の小節の n 拍目」に固定し、飛び出しから着弾までも SPIRIT.flightMs で一定 (awoken_rampage_v5.ts)。
//   ・抑揚 = 小節ごとに密度を変える:
//       1 ガブ・・・・・        (導入・噛みだけ)
//       2 ガブ・タ・タ・        (基本・等間隔)
//       3 ガブ・・赤・・        (溜め・赤は回避)
//       4 ガブ・タタ・・        (詰めた2連)
//       5 ガブタ・赤・・        (噛みの直後にもう1発弾いてから避ける)
//         🔴 赤 (回避) の後は次の弾きまで3拍以上あける。回避は押してから 0.2秒パリィを出せない (PLAYER.stepIFrameMs・全体の決まり)。
//            第6版の「ガブ・タ・赤・」は赤の2拍 (0.45秒) 後に噛みが来て、人の手では間に合わなかった (09-21 ユーザー実プレイ)。
//       6 ガブ・・タタ・        (山場・一息ためてから詰めた2連。09-21 ユーザー「最後の紫3つを2つに」で3連から減らした)
//       → 叩きつけ (次の小節の1拍目に着地) → 6拍後に締めの噛み。
//   ・足音 (壁に着く・蹴る) もなるべく拍に乗せた: 噛み→壁に着く = 約3拍、蹴る = 4.5拍、次の噛み = 6拍。
export const BEAT_SEC = 0.225
const SWOOP_Y = 30
// 次の小節の n 拍目に届く怨念。蹴り (跳び出し) から次の噛みまで = 1小節 + 噛みの飛行 (travel×BITE_STRIKE_PROGRESS)。
const SWOOP = { warning: .325, travel: .45, runWarning: .1, runTravel: .475 } as const
const nextBar = (beat: number, red = false): SpiritCue =>
  ({ arriveAfterKickMs: Math.round((SWOOP.warning + SWOOP.travel + SWOOP.runWarning + SWOOP.runTravel + SWOOP.travel * .75 + beat * BEAT_SEC) * 1000), ...(red ? { red } : {}) })
const BAR_EVEN: readonly SpiritCue[] = [nextBar(2), nextBar(4)]
const BAR_REST_RED: readonly SpiritCue[] = [nextBar(3, true)]
const BAR_QUICK: readonly SpiritCue[] = [nextBar(2), nextBar(3)]
const BAR_PARRY_RED: readonly SpiritCue[] = [nextBar(1), nextBar(3, true)]
const BAR_LATE_PAIR: readonly SpiritCue[] = [nextBar(3), nextBar(4)]
// 壁 → 主人公へ低く飛び込み噛み (到着点は構えの頭で主人公に合わせる) → 向こうの壁へ跳ぶ。
function crossing(to: number, wallY: number, spirits?: readonly SpiritCue[]): ChoreoLeg[] {
  return [
    { phase: 6, kind: 'bite', x: to, y: SWOOP_Y, arc: 0, warning: SWOOP.warning, travel: SWOOP.travel, ...(spirits ? { spirits } : {}) },
    { phase: 6, kind: 'run', x: to, y: wallY, arc: 30, warning: SWOOP.runWarning, travel: SWOOP.runTravel },
  ]
}
export const ROAM: readonly ChoreoLeg[] = [
  { phase: 1, kind: 'run', x: WALL_R, y: 190, arc: 50, warning: .35, travel: .45 },
  // 壁に着いてから4拍で噛みが当たる。
  { phase: 2, kind: 'bite', x: 480, y: 0, arc: 20, warning: .5625, travel: .45 },
  // 噛み → 4拍 → 壁に着く → 1拍 → 蹴る → 3拍 → 左の床へ着地 → 3拍 → 噛み → 1.5拍 → 二噛み目。
  { phase: 3, kind: 'run', x: WALL_R, y: 200, arc: 40, warning: .3375, travel: .45 },
  { phase: 3, kind: 'run', x: 150, y: 0, arc: 30, warning: .225, travel: .675 },
  { phase: 4, kind: 'bite', x: 480, y: 0, arc: 0, warning: .375, travel: .4 },
  { phase: 5, kind: 'follow', x: 480, y: 0, arc: 0, warning: .09, travel: .2 },
  // 二噛み目から6拍 (1小節) で最初の横切りの噛み。
  { phase: 6, kind: 'run', x: WALL_R, y: 150, arc: 60, warning: .15, travel: .4875 },
  ...crossing(WALL_L, 180, BAR_EVEN),
  ...crossing(WALL_R, 150, BAR_REST_RED),
  ...crossing(WALL_L, 190, BAR_QUICK),
  ...crossing(WALL_R, 140, BAR_PARRY_RED),
  ...crossing(WALL_L, 180, BAR_LATE_PAIR),
  ...crossing(WALL_R, 160),
  // 床技: 右の壁で溜めて中央へ叩きつける。着地 = 最後の噛みから7拍後 (最後の怨念の3拍後)。衝撃波が床を左右へ這う。
  { phase: 7, kind: 'run', x: 480, y: 0, arc: 40, warning: .5375, travel: .35, wave: true, glint: 'red' },
  // 衝撃波が通り過ぎるまで屈み、一瞬溜めて締めの噛みつき (着地から6拍後に当たる)。
  { phase: 7, kind: 'bite', x: 480, y: 0, arc: 30, warning: 1.0125, travel: .45, glint: 'white' },
]
export const ROAM_NAMES = ['右の壁へ跳び付く', '壁で溜めて飛び込み噛みつき', '右の壁の上へ → 蹴って弧を描き左へ', '左から突進噛みつき', '二噛み目', '壁を蹴って3往復 (横切りざまに噛む)', '中央へ叩きつけ → 一瞬溜めて飛び込み噛みつき']
export const ROAM_END = ROAM.reduce((t, leg) => t + leg.warning + leg.travel, 0)
// 暴れ出す前に1回だけ雄叫びを上げる (09-20 ユーザー「最初の咆哮はほぼ無意味→１回雄叫びを上げたら暴れ出す」)。
//   振付の時計はこの後から数える (雄叫びの間は台本の 0 秒で止まる)。
export const CHOREO_ROAR_SEC = 1.1
// 本編の技の尺: 雄叫び＋最初の区間の構え = telegraph、残り全部 = active。
export const CHOREO_CHARGE_MS = Math.round((CHOREO_ROAR_SEC + ROAM[0]!.warning) * 1000)
// active の頭は振付の何秒目か (= 最初の区間の構えの終わり)。
export const CHOREO_ACTIVE_START_SEC = ROAM[0]!.warning
// 光る合図は跳び出しのこの秒数前。光ってから動き出すまでが読みの時間。
export const GLINT_LEAD_SEC = 0.45
export const GLINT_SHOW_SEC = 0.35
// 光る合図の時刻 (振付の秒) と色。
export function glintTimes(): { at: number; color: 'red' | 'white' }[] {
  const out: { at: number; color: 'red' | 'white' }[] = []
  let start = 0
  for (const leg of ROAM) {
    if (leg.glint) out.push({ at: start + leg.warning - GLINT_LEAD_SEC, color: leg.glint })
    start += leg.warning + leg.travel
  }
  return out
}
// 体が壁・床に触れる瞬間 (振付の秒)。揺れ・粒・音を出す所 (09-21 総評 B-2「接触の痕跡がゼロ」)。判定には関わらない。
//   wall_kick/ground_kick = 跳び出し、wall_land/ground_land = 着いた瞬間、slam = 床技の叩きつけ。
//   横切りの噛み (床すれすれで終わる) と、床から床への直線の突進の終わりは「着地」ではないので出さない。
export type ChoreoContactKind = 'wall_kick' | 'wall_land' | 'ground_kick' | 'ground_land' | 'slam'
const legEndsOnWall = (leg: ChoreoLeg | undefined): boolean =>
  !!leg && leg.kind === 'run' && leg.y > 0 && (leg.x === WALL_R || leg.x === WALL_L)
export function contactTimes(): { at: number; kind: ChoreoContactKind; pass: number }[] {
  const out: { at: number; kind: ChoreoContactKind; pass: number }[] = []
  let start = 0
  ROAM.forEach((leg, pass) => {
    const prev = ROAM[pass - 1]
    const fromGround = !prev || prev.y === 0
    const kick = start + leg.warning, land = kick + leg.travel
    if (legEndsOnWall(prev)) out.push({ at: kick, kind: 'wall_kick', pass })
    else if (fromGround) out.push({ at: kick, kind: 'ground_kick', pass })
    if (legEndsOnWall(leg)) out.push({ at: land, kind: 'wall_land', pass })
    else if (leg.y === 0 && leg.wave) out.push({ at: land, kind: 'slam', pass })
    else if (leg.y === 0 && !(fromGround && leg.arc === 0)) out.push({ at: land, kind: 'ground_land', pass })
    start += leg.warning + leg.travel
  })
  return out
}
export const CHOREO_ACTIVE_MS = Math.round((ROAM_END - ROAM[0]!.warning) * 1000)
// 噛みつきの着地点は「口が主人公に重なる所」= 主人公の x から口の前方距離だけ手前。
//   🔴 2026-09-26: 85 → 140。旧 85 は口が前方85pxにある前提だったが、噛みの絵の口は着地で前方約174px
//   (bite_contact / swoop_bite_0920、awoken_bite_mouth.ts の実測)。85 だと口は主人公の50〜90px先を
//   頭の上から通り過ぎ、胴 (たてがみ) が触れた所で噛まれていた (docs/handoffs/2026-09-26-awoken-hit-validity.md NG③)。
//   140 = 窓 (噛みの最後の40px) の途中で、口が主人公の頭の高さへ降りてきた所で主人公の x に重なる値
//   (壁から・床からの全10本を実描画で確かめた。bite_step の二噛み目も同じ値で口が主人公の上に来る)。
export const BITE_OFFSET_PX = 140
// 噛みつきの当たりの控え (本当の当たりは hits.ts が毎tick 絵の口の位置から出す = mouth: true)。
//   横は口の周り (着地で前方約174 − 口の窓の後ろ15〜前30 を踏み込み40px分の幅で覆う)、縦は口の高さ。
export const BITE_STRIKE = { localFromPx: 120, localToPx: 205 } as const
export const BITE_BAND = { fromPx: 50, toPx: 140 } as const
// 怨念: 壁を蹴った所に留まってから飛ぶ。留まる長さは距離で変え、**決まった拍で主人公へ届く**ように揃える
//   (どこに立っていても届く拍が同じ＝覚えられるリズム)。弱い追尾 (ユーザー裁定 09-19)。仮値。
//   届く拍は ROAM の spirits (蹴ってからの ms)。minHangMs = 出所を見せる最短の溜め。
//   09-21 第6版: 「一定の速さ」をやめて「一定の飛行時間」にした (近い壁からは 0.15秒で着弾し、見てからでは反応できなかった)。
//   09-21 第7版: それだけだと玉ごとに速さが最大7倍違い、目で読めず暗記頼みになった (ユーザー実プレイ「一定じゃないのが気持ち悪い・
//     見て弾くことができない」)。→ **毎回同じ距離から・同じ速さで・同じ時間で飛び込む**:
//     壁で生まれる (wallHangMs) → 主人公の斜め上の決まった席へ漂って留まる (主人公が動けば席も付いて行く) → 光って飛び込む。
//     飛び込みは主人公から見た動きで決める (歩いても回避しても、見え方と届く時刻が変わらない)。着弾の lockMs 前に追うのをやめる。
//   flightMs = 飛び出し→着弾 (2.5拍 = 飛び出しの合図が「弾く瞬間」と重ならない半拍ずれ)。diveGapPx = 飛び込む距離 (触れる点まで)。
//   slotDeg = 同時に留まる玉の席 (水平からの角度)。sideRoomPx = 壁側にこれだけ余地が無ければ反対側の席に着く。
//   speedPxPerSec は拍を過ぎた後に抜けていく最低の速さ。
export const SPIRIT = { minHangMs: 150, flightMs: 562.5, lockMs: 50,
  diveGapPx: 300, slotDeg: [25, 42, 60], wallHangMs: 300, settleMs: 200, sideRoomPx: 200,
  speedPxPerSec: 600, turnRadPerSec: 0.5, homingMs: 400,
  rangePx: 1000, widthPx: 26, spawnUpPx: 60, reflectSpeedPxPerSec: 650, reflectLifeMs: 3000,
  // 09-21: 発生点を体の中心から部屋側 (頭の先) へ出す。体の上に生まれると王の絵に隠れて、赤 (弾けない) が見えなかった (総評 A-2)。
  spawnRoomwardPx: 120, pairUpPx: 52 } as const
// 床技の衝撃波 (仮値): 床すれすれを左右へ這う。高さ 0〜heightPx、弾けない。
//   着地点から startOffsetPx 外側で生まれる (真下に立つと両側の波が同時に来て抜けられない形を避ける)。
export const WAVE = { speedPxPerSec: 600, heightPx: 34, widthPx: 20, rangePx: 700, startOffsetPx: 50 } as const
// 各怨念 (と衝撃波) の出る時刻・届く時刻 (振付開始からの秒) と、弾けるかどうか。
export type SpiritBeat = { spawnAt: number; arriveAt: number; red: boolean; wave?: boolean }
export function spiritSchedule(): SpiritBeat[] {
  const beats: SpiritBeat[] = []
  let start = 0
  for (const leg of ROAM) {
    const kick = start + leg.warning
    for (const cue of leg.spirits ?? []) beats.push({ spawnAt: kick, arriveAt: kick + cue.arriveAfterKickMs / 1000, red: !!cue.red })
    if (leg.wave) beats.push({ spawnAt: kick + leg.travel, arriveAt: kick + leg.travel, red: true, wave: true })
    start += leg.warning + leg.travel
  }
  return beats
}
export function spiritTimes(): number[] { return spiritSchedule().map(b => b.spawnAt) }
// 高さがこれを超える間は、主人公の攻撃が届かない (主人公の高さ120の6割)。
export const AIRBORNE_UNHITTABLE_PX = 72
// 崩れた時に空中から床へ落ちる速さ (1tickあたり)。230px を約0.25秒。
export const STAGGER_FALL_PX_PER_TICK = 16
// 噛みつきの口が閉じる進行度。ここから区間の終わりまでが当たりの窓。
export const BITE_STRIKE_PROGRESS = .75
// 噛みの横移動は2段: BITE_STRIKE_PROGRESS までに「口が主人公へ届く所」(到着点の BITE_APPROACH_PX 手前) へ着き、
//   残りで到着点まで詰める。こうすると当たる瞬間が距離によらず同じになる (09-21 ユーザー裁定
//   「跳び出してから当たるまでの時間を固定・距離で本体の速さが変わるのは OK」)。
//   それまでは等速で、近い壁からは 0.23秒・遠い壁からは 0.36秒と立ち位置で拍がずれていた。
export const BITE_APPROACH_PX = 40
export function biteX(from: number, to: number, progress: number): number {
  const d = Math.abs(to - from)
  if (d <= BITE_APPROACH_PX) return from + (to - from) * progress
  const near = to - Math.sign(to - from) * BITE_APPROACH_PX
  return progress < BITE_STRIKE_PROGRESS
    ? from + (near - from) * (progress / BITE_STRIKE_PROGRESS)
    : near + (to - near) * ((progress - BITE_STRIKE_PROGRESS) / (1 - BITE_STRIKE_PROGRESS))
}

// 横は等速・縦は放物線 = 重力のある跳び方 (吊るされて見えない)。arc は山の高さ。
export function legHeight(fromHeight: number, toHeight: number, arc: number, u: number): number {
  return fromHeight + (toHeight - fromHeight) * u + 4 * arc * u * (1 - u)
}

// 壁にしがみついている位置か (床より上で、x が左右どちらかの壁)。
export const isWallAnchor = (x: number, y: number, mirror: boolean): boolean =>
  y > 0 && (x === mirrorX(WALL_R, mirror) || x === mirrorX(WALL_L, mirror))

// 右から入る振付を基準に、左から入る時は x を鏡写しにする (アリーナ幅 960 基準)。
//   台本の x → 世界の x。舞台が広い時は中央から横に伸ばし、壁にしがみつく位置 (WALL_L/WALL_R) が今の柱の面 − 106 に来るようにする。
export const mirrorX = (x: number, mirror: boolean): number => {
  const w = choreoWallSurfaces(), c = (w.left + w.right) / 2
  const s = (w.right - WALL_FOOT_INSET_PX - c) / (WALL_R - 480)
  const v = s === 1 && c === 480 ? x : Math.round(c + (x - 480) * s)
  return mirror ? 2 * c - v : v
}

// 各区間の既定の到着点と向き。噛みつきの到着点・向きは構え開始時に上書きされる。
export function defaultTargets(mirror: boolean): number[] {
  return ROAM.map(leg => mirrorX(leg.x, mirror))
}
export function defaultFacings(mirror: boolean, startX: number): number[] {
  return ROAM.map((leg, i) => {
    const from = i === 0 ? startX : mirrorX(ROAM[i - 1]!.x, mirror)
    return mirrorX(leg.x, mirror) < from ? -1 : 1
  })
}

export type ChoreoPose = {
  x: number
  height: number
  from: number
  to: number
  fromHeight: number
  toHeight: number
  arc: number
  pass: number
  phase: number
  kind: ChoreoLeg['kind']
  facing: number
  active: boolean
  recovery: boolean
  striking: boolean
  warning: boolean
  local: number
  progress: number
  name: string
}

// t = 振付の開始 (最初の構えの頭) からの秒。targets/facings は区間ごとの到着点と向き。
export function choreoPoseAt(t: number, startX: number, targets: readonly number[], facings: readonly number[]): ChoreoPose {
  let start = 0, pass = 0
  while (pass < ROAM.length - 1 && t >= start + ROAM[pass]!.warning + ROAM[pass]!.travel - 1e-8) {
    start += ROAM[pass]!.warning + ROAM[pass]!.travel; pass++
  }
  const leg = ROAM[pass]!, prev = ROAM[pass - 1]
  const from = pass > 0 ? targets[pass - 1]! : startX, fromHeight = prev?.y ?? 0
  const to = targets[pass]!
  const local = t - start, progress = Math.max(0, Math.min(1, (local - leg.warning) / leg.travel))
  const recovery = t >= ROAM_END - 1e-8, active = !recovery && local >= leg.warning - 1e-8
  const height = legHeight(fromHeight, leg.y, leg.arc, progress)
  return { x: leg.kind === 'run' ? from + (to - from) * progress : biteX(from, to, progress), height, from, to, fromHeight, toHeight: leg.y,
    facing: leg.kind === 'run' ? (to < from ? -1 : 1) : facings[pass]!, arc: leg.arc, pass, phase: leg.phase, kind: leg.kind, active, recovery,
    striking: active && leg.kind !== 'run' && progress >= BITE_STRIKE_PROGRESS,
    warning: !active && !recovery, local, progress, name: ROAM_NAMES[leg.phase - 1]! }
}

// 各噛みつき区間の当たりの窓 (振付開始からの秒)。
export function biteWindows(): { pass: number; from: number; to: number }[] {
  const windows: { pass: number; from: number; to: number }[] = []
  let start = 0
  ROAM.forEach((leg, pass) => {
    if (leg.kind !== 'run') {
      windows.push({ pass, from: start + leg.warning + leg.travel * BITE_STRIKE_PROGRESS, to: start + leg.warning + leg.travel })
    }
    start += leg.warning + leg.travel
  })
  return windows
}
